/**
 * EU-642 — Identificador ANÔNIMO do dispositivo para o Mixpanel ID Merge
 * (Simplified API — RN-01 registrada na issue).
 *
 * Um único id por dispositivo/navegador (RN-02/03), persistido em:
 *   - `localStorage[eureca_mixpanel_anonymous_id]` — fonte canônica na origem
 *     do SPA (continuidade com os ids já emitidos antes do EU-642);
 *   - cookie first-party `eu_anid` no domínio pai (`.eureca.dev`/`.eureca.me`)
 *     — é o canal que leva o id até o Keycloak (`sso.*`): o SPI (event
 *     listener) lê o cookie e carimba `anonymousId` nos eventos de auth
 *     (`login_success`/`register_success`/`recover_password_success`), que o
 *     BFF envia ao Mixpanel como `$device_id` (RN-04/07).
 *
 * No logout o id é DESCARTADO (RN-09) — storage + cookie limpos; o próximo
 * evento gera um id fresco, então outro usuário no mesmo dispositivo não é
 * fundido ao anterior.
 *
 * Nunca lança: em modo privado / storage bloqueado degrada para um id em
 * memória (estável dentro da sessão da página).
 */

const STORAGE_KEY = 'eureca_mixpanel_anonymous_id';
const COOKIE_NAME = 'eu_anid';
/** 1 ano; o Max-Age é re-escrito a cada evento, então o cookie é rolling. */
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

let inMemoryFallback: string | null = null;

/**
 * Domínio pai registrável (`.eureca.dev` / `.eureca.me`) — mesmo critério do
 * cookie de UTM (`mixpanel-context.ts`). `localhost`/IP → host-only.
 */
function cookieParentDomain(): string | undefined {
  const host = window.location.hostname;
  if (host === 'localhost' || /^[\d.]+$/.test(host)) return undefined;
  const parts = host.split('.');
  if (parts.length < 2) return undefined;
  return '.' + parts.slice(-2).join('.');
}

function readAnonCookie(): string | null {
  try {
    const parts = document.cookie ? document.cookie.split(';') : [];
    for (const part of parts) {
      const p = part.trim();
      const eq = p.indexOf('=');
      if (eq > 0 && p.slice(0, eq) === COOKIE_NAME) {
        const v = decodeURIComponent(p.slice(eq + 1));
        return v.length > 0 ? v : null;
      }
    }
  } catch {
    /* cookies bloqueados */
  }
  return null;
}

function writeAnonCookie(value: string): void {
  try {
    const domain = cookieParentDomain();
    const domainAttr = domain ? `; Domain=${domain}` : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${domainAttr}${secure}`;
  } catch {
    /* cookies bloqueados — o SPI fica sem $device_id nos eventos de auth */
  }
}

function expireAnonCookie(): void {
  try {
    const domain = cookieParentDomain();
    const domainAttr = domain ? `; Domain=${domain}` : '';
    document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax${domainAttr}`;
  } catch {
    /* ignore */
  }
}

/**
 * Devolve o id anônimo do dispositivo, gerando na primeira chamada.
 *
 * Prioridade (INC-01 round 2, QA 17/08): **cookie `eu_anid` PRIMEIRO** — ele é
 * a fonte canônica compartilhada com o Keycloak (o tema em `sso.*` também o
 * escreve, e o SPI o lê nos eventos de auth). O localStorage é só espelho de
 * continuidade: vale quando NÃO há cookie e NUNCA sobrescreve um cookie
 * existente. A ordem antiga (localStorage primeiro + re-espelho incondicional)
 * fazia uma aba antiga do SPA ressuscitar o id do usuário anterior por cima do
 * id fresco que o tema mintou — dois usuários compartilhavam `$device_id` e o
 * Mixpanel rejeitava o merge (errAnonDistinctIdAssignedAlready).
 */
export function getOrCreateMixpanelAnonymousId(): string {
  const fromCookie = readAnonCookie();
  if (fromCookie) {
    try {
      if (localStorage.getItem(STORAGE_KEY) !== fromCookie) {
        // Drena o reservatório divergente do SPA — o cookie vence sempre.
        localStorage.setItem(STORAGE_KEY, fromCookie);
      }
    } catch {
      /* storage bloqueado — segue só com o cookie */
    }
    return fromCookie;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length > 0) {
      writeAnonCookie(stored);
      return stored;
    }
  } catch {
    /* storage bloqueado */
  }

  if (inMemoryFallback) return inMemoryFallback;

  const fresh = crypto.randomUUID();
  inMemoryFallback = fresh;
  try {
    localStorage.setItem(STORAGE_KEY, fresh);
  } catch {
    /* modo privado — fica no fallback em memória */
  }
  writeAnonCookie(fresh);
  return fresh;
}

/**
 * RN-09 — descarta o id anônimo no logout. Não gera o substituto aqui: o
 * próximo evento (provavelmente de OUTRO usuário no mesmo dispositivo) chama
 * `getOrCreateMixpanelAnonymousId()` e nasce com um id fresco.
 */
export function resetMixpanelAnonymousId(): void {
  inMemoryFallback = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  expireAnonCookie();
}
